import * as React from "react";

import {
  Modal,
  Icon,
  Text,
  Flex,
  Select,
  Radio,
  Checkbox,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@theorgchartdev/storybook";
import {
  mouseOptions,
  measurementsOptions,
  themeOptions,
  localStorageKeys,
  titleTextProps,
  labelSectionProps,
  sectionTextProps,
  optionGroupProps
} from "./PreferencesUtils";
import * as StateLib from "@/Utils/StateLib";
import CentralizedModalContainer from "../../../../Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import { Globals } from "@coreapp/CoreApp/Globals";
import { appstr } from "@coreapp/utils/appstr";
import { globalLanguageCodes, enumMFAState, enumFeaturesList } from "@coreapp/constants";
import { Cappstr } from "@coreapp/utils/Cappstr";
import { getFeatureAccess } from "@/Utils/Misc";

export const PreferencesModal = props => {
  const { show, setShow } = props;
  const [currentOptions, setCurrentOptions] = React.useState({
    language: localStorage.getItem(localStorageKeys.language) || "en",
    mouse: localStorage.getItem(localStorageKeys.mouse) || "vscroll",
    units: localStorage.getItem(localStorageKeys.units) || "SI",
    theme: localStorage.getItem(localStorageKeys.theme) || "lightmode",
    rightclick: localStorage.getItem("rightclick") || "true"
  });
  const userInfo = StateLib.GetUserInfo();
  const [showConfirmationModal, setShowConfirmationModal] = React.useState(false);
  const wsMgr = StateLib.GetWSMgr();
  let mfaActive = userInfo?.fMFAState;
  let showLanguage = Globals._uiConfig.fEnableLanguage;

  const handleChange = (key, value) => {
    setCurrentOptions(prevOptions => ({ ...prevOptions, [key]: value }));
  };

  const SaveCurrentOptions = () => {
    Object.keys(currentOptions).map(key => {
      localStorage.setItem(localStorageKeys[key], currentOptions[key]);
    });

    userInfo.fLanguage = currentOptions.language;
    Globals._stringMgr.ResetStringMgr();

    const wsmgr = StateLib.GetWSMgr();
    wsmgr.UpdateUserInfoRFC(null, userInfo, false, false, false);

    setShow(false);
  };

  const BuildSelectOptions = options => {
    return options
      .filter(option => option !== "zo")
      .map(option => ({
        label: globalLanguageCodes[option],
        value: option,
        clickHandler: () => {
          handleChange("language", option);
        }
      }));
  };

  const openMfaModal = () => {
    let showConfirmation = userInfo?.fMFAState == enumMFAState.enabledAuth;
    if (showConfirmation === true) setShowConfirmationModal(true);
    else wsMgr.BroadcastTopic("MFAModal", true);
  };

  const mfaNotification = (mfastatus, user) => {
    let content;
    let curMFACode = wsMgr.GetMFAState(user, true);

    if (mfastatus) {
      content = (
        <>
          <Text
            colorModifier={"success"}
            element={"small"}
            modifier={"bold"}
            size={"xsmall"}
            type={"display"}
          >
            {appstr("Enabled")}
          </Text>
        </>
      );
    } else if (curMFACode === enumMFAState.overrideMFA) {
      content = (
        <>
          <Text
            colorModifier={"warning"}
            element={"small"}
            modifier={"bold"}
            size={"xsmall"}
            type={"display"}
          >
            {appstr("Bypassed")}
          </Text>
        </>
      );
    } else {
      content = (
        <>
          <Text
            colorModifier={"error"}
            element={"small"}
            modifier={"bold"}
            size={"xsmall"}
            type={"display"}
          >
            {appstr("Disabled")}
          </Text>
        </>
      );
    }
    return <>{content}</>;
  };

  const BuildRadioSettings = (settings, key, currentValue, feature, dataTestIdPrefix) => {
    return (
      <Flex direction={"row"} gap={"8px"}>
        {settings.map(item => (
          <Radio
            key={`lang-select-${item}`}
            id={`preferences-${key}-${item}`}
            label={`${appstr(item)}`}
            value={item}
            checked={item === currentValue}
            onChange={() => handleChange(key, item)}
            disabled={item === "darkmode" || !getFeatureAccess(feature)}
            data-testid={`${dataTestIdPrefix}-${item}`}
          />
        ))}
      </Flex>
    );
  };

  return (
    <React.Fragment>
      <Modal
        id={"ust-pref-modal"}
        show={show}
        maxWidth="medium"
        minWidth="medium"
        onClose={() => {}}
        fullScreen={false}
        centerScreen={true}
      >
        <DialogTitle divider={true} onClose={() => setShow(false)}>
          <span className="ocn-explorer-icon-label">
            <Icon size="medium" name="user" />
            {appstr("orgprefs")}
          </span>
        </DialogTitle>

        <Flex {...labelSectionProps} padding={"8px 0"}>
          {showLanguage && (
            <DialogContent>
              <Flex {...labelSectionProps}>
                <Text {...titleTextProps}>{appstr("App preferences")}</Text>
                <Flex {...optionGroupProps} alignItems={"center"} witdh={"40%"}>
                  <Select
                    value={currentOptions.language}
                    options={BuildSelectOptions(Object.keys(globalLanguageCodes))}
                    label={appstr("Language")}
                    inputOnly={false}
                    disabled={!getFeatureAccess(enumFeaturesList.languageSwitcher)}
                    dataTestId={"chart-mode-toolbar-preferences-language"}
                  />
                </Flex>
              </Flex>
            </DialogContent>
          )}

          <DialogContent>
            <Flex {...labelSectionProps}>
              <Text {...titleTextProps}>{appstr("Mouse controls")}</Text>
              <Text {...sectionTextProps}>{appstr("Mouse wheel")}:</Text>
              {BuildRadioSettings(
                mouseOptions,
                "mouse",
                currentOptions.mouse,
                "mouseControls",
                "chart-mode-toolbar-preferences-mouse"
              )}
              <Flex {...optionGroupProps}>
                <Checkbox
                  id={"ust-pref-rc-chkbx"}
                  data-testid={"chart-mode-toolbar-preferences-rightclick"}
                  size="medium"
                  checked={currentOptions.rightclick === "true"}
                  onToggle={() => {
                    handleChange(
                      "rightclick",
                      currentOptions.rightclick === "true" ? "false" : "true"
                    );
                  }}
                  label={appstr("Enable Right-Click")}
                  disabled={!getFeatureAccess(enumFeaturesList.mouseControls)}
                />
              </Flex>
            </Flex>
          </DialogContent>

          <DialogContent>
            <Flex {...labelSectionProps}>
              <Text {...titleTextProps}>{appstr("Measurements")}</Text>
              {BuildRadioSettings(
                measurementsOptions,
                "units",
                currentOptions.units,
                enumFeaturesList.measurements,
                "chart-mode-toolbar-preferences-measurements"
              )}
            </Flex>
          </DialogContent>

          <DialogContent>
            <Flex {...labelSectionProps}>
              <Text {...titleTextProps}>{appstr("Theme")}</Text>
              {BuildRadioSettings(
                themeOptions,
                "theme",
                currentOptions.theme,
                enumFeaturesList.theme,
                "chart-mode-toolbar-preferences-theme"
              )}
            </Flex>
          </DialogContent>

          <DialogContent>
            <Flex {...labelSectionProps}>
              <Flex direction={"row"} gap="8px">
                <Text {...titleTextProps}>{appstr("multifactorauth")}</Text>
                {mfaNotification(mfaActive, userInfo)}
              </Flex>
              <Flex direction={"row"} gap="8px">
                <Button
                  icon={"key"}
                  modifier={"secondary"}
                  iconProps={{ name: "key", modifier: "white" }}
                  size={"medium"}
                  isMaxWidth={false}
                  onClick={() => openMfaModal()}
                  disabled={!getFeatureAccess(enumFeaturesList.MFA)}
                  dataTestId={"chart-mode-toolbar-preferences-mfa"}
                >
                  {appstr("Multi-Factor Authentication Settings")}
                </Button>
              </Flex>
            </Flex>
          </DialogContent>

          {showConfirmationModal && (
            <CentralizedModalContainer show={show}>
              <Modal
                id="example-modal"
                show={show}
                minWidth={"medium"}
                maxWidth={"medium"}
                fillWidth={true}
                centerScreen={true}
              >
                <DialogTitle divider={true} onClose={() => setShowConfirmationModal(false)}>
                  <span className={"ocn-explorer-icon-label"}>
                    <Icon size={"medium"} name={"lock"} />
                    {appstr("MFA Setup")}
                  </span>
                </DialogTitle>
                <DialogContent>
                  <Flex direction={"column"} gap={"12px"}>
                    <Flex direction={"row"} gap={"12px"}>
                      <span className={"ocn-explorer-icon-label"}>
                        <Icon size={"medium"} name={"check"} modifier={"success"} />
                        <Text modifier={"bold"} size={"medium"}>
                          {appstr("mfaActive")}
                        </Text>
                      </span>
                    </Flex>
                    <Text>{appstr("MFA Enabled")}</Text>
                    <Button
                      modifier={"primary"}
                      className={"oc-special-secondary"}
                      size={"medium"}
                      onClick={() => {
                        wsMgr.BroadcastTopic("MFAModal", true);
                        setShowConfirmationModal(false);
                      }}
                      dataTestId={"chart-mode-toolbar-preferences-mfa-reset"}
                    >
                      {appstr("resetAuth")}
                    </Button>
                  </Flex>
                </DialogContent>
              </Modal>
            </CentralizedModalContainer>
          )}
        </Flex>
        <DialogActions size="medium">
          <DialogActions.Tertiary
            dataTestId={"chart-mode-toolbar-preferences-cancel"}
            onClick={() => setShow(false)}
          >
            {appstr("Cancel")}
          </DialogActions.Tertiary>
          <DialogActions.Primary
            dataTestId={"chart-mode-toolbar-preferences-update"}
            onClick={SaveCurrentOptions}
          >
            {appstr("Update")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </React.Fragment>
  );
};
